import { getCreateStoreHook } from '@jira/polaris__lib-react-sweet-state-utils/src/getCreateStoreHook.tsx';
import { getCreateHigherLevelHook } from '@jira/polaris__lib-react-sweet-state-utils/src/createJpdContainer.tsx';
import { getStoreHandlersWithStatefullContainerProps } from '@jira/polaris__lib-react-sweet-state-utils/src/createContainerWithContext.tsx';
import type { SelectorCreator } from '@jira/polaris__lib-react-sweet-state-utils/src/SelectorCreator.types.tsx';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import {
	createActionsHook,
	createContainer,
	createStore,
	type BoundActions,
	type HookActionsFunction,
	type HookStateFunction,
	type OverrideContainerComponent,
	type Selector,
	type Store,
} from '@atlassian/react-sweet-state';
import { syncFieldsIssueTypes } from './syncFieldsIssueTypes.tsx';
import { loadFields } from './loadFields.tsx';
import { throttledFetchSnippetProviders } from './throttledFetchSnippetProviders.tsx';
import { actions, type Actions } from './fieldActions.tsx';
import type { Props, State } from './FieldsUpdate.types.tsx';

export const initialState: State = {
	meta: {
		initialized: false,
		loading: false,
		loadingSnippetProviders: false,
		error: undefined,
		loadingProps: undefined,
		projectIsNotOnboardedError: false,
		scheduleFieldsReload: false,
		connectionsLoadingFieldsAssociations: [],
	},
	fields: {},
	fieldValueDecorations: {},
	snippetProviders: undefined,
	containerProps: undefined,
	throttledFetchSnippetProviders: undefined,
	newlyAddedGlobalFields: [],
	connectionsFieldsAssociations: {},
	lastFieldsUpdate: null,
};

export const FieldStore: Store<State, Actions, Props> = createStore<State, Actions, Props>({
	name: 'PolarisFieldStore',
	initialState,
	actions,
});

export const FieldContainer: OverrideContainerComponent<Props> = createContainer<
	State,
	Actions,
	Props
>(FieldStore, {
	displayName: 'PolarisFieldStoreContainer',
	getIsConcurrentSafe: () => true,
	...getStoreHandlersWithStatefullContainerProps({
		onInit:
			() =>
			({ dispatch }) => {
				dispatch(loadFields());
				dispatch(throttledFetchSnippetProviders());
			},
		onUpdate:
			() =>
			({ dispatch, getState }, props) => {
				const { prevContainerProps } = getState();
				if (
					prevContainerProps?.issueTypeIds &&
					props.issueTypeIds !== prevContainerProps.issueTypeIds
				) {
					dispatch(syncFieldsIssueTypes());
				}

				dispatch(loadFields());
				dispatch(throttledFetchSnippetProviders());
			},
	}),
});

export const useFieldActions: HookActionsFunction<BoundActions<State, Actions>> =
	createActionsHook(FieldStore);
export const createFieldHook: <TValue, THookProps = void>(
	selector: Selector<State, THookProps, TValue>,
) => HookStateFunction<TValue, THookProps> = getCreateStoreHook(FieldStore);
export const createHigherLevelFieldHook: <
	TValue,
	SelectorCreatorArgs extends ReadonlyArray<unknown>,
>(
	selectorCreator: SelectorCreator<State, Props, TValue, SelectorCreatorArgs>,
) => (...rest: SelectorCreatorArgs) => TValue = getCreateHigherLevelHook<Props, State, Actions>(
	FieldStore,
);
